import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import api from '@/lib/api';
import { useLanguage } from '@/lib/i18n';
import { useSalonProfile } from '@/contexts/SalonProfileContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Skeleton } from '@/components/ui/skeleton';
import { Checkbox } from '@/components/ui/checkbox';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Search, Plus, Phone, Mail, Edit, Award, IndianRupee, CalendarDays, MoreVertical, UserX, RotateCcw, FileText, CalendarPlus, Star } from 'lucide-react';

const DEFAULT_TIER_THRESHOLDS = { bronze_max: 2000, silver_max: 5000, gold_max: 10000 };

// Tier from points using salon thresholds (from Settings → Billing). Never use stored loyalty_tier for display.
function calculateTier(points, thresholds = DEFAULT_TIER_THRESHOLDS) {
  const p = Number(points) || 0;
  const t = thresholds || DEFAULT_TIER_THRESHOLDS;
  if (p > (t.gold_max ?? 10000)) return 'Diamond';
  if (p > (t.silver_max ?? 5000)) return 'Gold';
  if (p > (t.bronze_max ?? 2000)) return 'Silver';
  return 'Bronze';
}

function getNextTierInfo(points, thresholds = DEFAULT_TIER_THRESHOLDS) {
  const p = Number(points) || 0;
  const t = thresholds || DEFAULT_TIER_THRESHOLDS;
  const bMax = t.bronze_max ?? 2000;
  const sMax = t.silver_max ?? 5000;
  const gMax = t.gold_max ?? 10000;
  if (p > gMax) return { next: null, needed: 0 };
  if (p > sMax) return { next: 'Diamond', needed: gMax + 1 - p };
  if (p > bMax) return { next: 'Gold', needed: sMax + 1 - p };
  return { next: 'Silver', needed: bMax + 1 - p };
}

function getTierProgressPercent(points, thresholds = DEFAULT_TIER_THRESHOLDS) {
  const p = Number(points) || 0;
  const t = thresholds || DEFAULT_TIER_THRESHOLDS;
  const bMax = t.bronze_max ?? 2000;
  const sMax = t.silver_max ?? 5000;
  const gMax = t.gold_max ?? 10000;
  if (p > gMax) return 100;
  if (p > sMax) return Math.min(100, Math.max(0, ((p - (sMax + 1)) / (gMax - sMax)) * 100));
  if (p > bMax) return Math.min(100, Math.max(0, ((p - (bMax + 1)) / (sMax - bMax)) * 100));
  return Math.min(100, Math.max(0, (p / (bMax + 1)) * 100));
}

const tierBadgeStyles = {
  Bronze: { background: '#FDE8D0', color: '#92400E' },
  Silver: { background: '#F1F5F9', color: '#475569' },
  Gold: { background: '#FEF3C7', color: '#92600A' },
  Diamond: { background: '#EDE9FE', color: '#5B21B6' },
};

const emptyForm = { name: '', phone: '', email: '', birthday: '', gender: '', notes: '' };

export default function Customers() {
  const [customers, setCustomers] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [selectedCustomerId, setSelectedCustomerId] = useState(null);
  const [profileData, setProfileData] = useState(null);
  const [profileLoading, setProfileLoading] = useState(false);
  const [profileError, setProfileError] = useState(null);
  const [fieldErrors, setFieldErrors] = useState({});
  const [includeInactive, setIncludeInactive] = useState(false);
  const [deactivateConfirmId, setDeactivateConfirmId] = useState(null);
  const [extraVisits, setExtraVisits] = useState([]);
  const [visitsLoadMoreLoading, setVisitsLoadMoreLoading] = useState(false);
  const { t } = useLanguage();
  const navigate = useNavigate();
  const { profile } = useSalonProfile();
  const tierThresholds = profile?.tier_thresholds || DEFAULT_TIER_THRESHOLDS;
  const user = JSON.parse(localStorage.getItem('salon_user') || '{}');
  const canDeactivate = ['owner', 'manager'].includes((user.role || '').toLowerCase());

  const loadCustomers = async (includeInactiveParam) => {
    const inc = includeInactiveParam !== undefined ? includeInactiveParam : includeInactive;
    try {
      const r = await api.get(`/customers?include_inactive=${inc}`);
      setCustomers(r.data);
    } catch { toast.error(t('cust_load_error')); }
    finally { setLoading(false); }
  };

  useEffect(() => {
    setLoading(true);
    loadCustomers(includeInactive);
  }, [includeInactive]);

  const handleSave = async () => {
    const err = {};
    const name = (form.name || '').trim();
    if (name.length < 2) err.name = t('val_name_min');
    const phone = (form.phone || '').replace(/\D/g, '');
    if (phone.length !== 10) err.phone = t('val_phone_10');
    if ((form.email || '').trim()) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(form.email.trim())) err.email = t('val_email_invalid');
    }
    if (Object.keys(err).length > 0) {
      setFieldErrors(err);
      toast.error(t('cust_req'));
      return;
    }
    setFieldErrors({});
    setSaving(true);
    const payload = { ...form, phone };
    try {
      if (editingId) {
        await api.put(`/customers/${editingId}`, payload);
        toast.success(t('cust_updated'));
      } else {
        await api.post('/customers', payload);
        toast.success(t('cust_added'));
      }
      setDialogOpen(false);
      setForm(emptyForm);
      setEditingId(null);
      loadCustomers(includeInactive);
    } catch { toast.error(t('cust_save_error')); }
    finally { setSaving(false); }
  };

  const openEdit = (c) => {
    setFieldErrors({});
    setEditingId(c.id);
    setForm({ name: c.name, phone: c.phone, email: c.email || '', birthday: c.birthday || '', gender: c.gender || '', notes: c.notes || '' });
    setDialogOpen(true);
  };

  const loadProfile = useCallback(async (customerId) => {
    if (!customerId) return;
    setProfileLoading(true);
    setProfileError(null);
    try {
      const r = await api.get(`/customers/${customerId}/profile`);
      setProfileData(r.data);
    } catch {
      setProfileError(true);
      setProfileData(null);
    } finally {
      setProfileLoading(false);
    }
  }, []);

  useEffect(() => {
    if (selectedCustomerId) {
      loadProfile(selectedCustomerId);
    } else {
      setProfileData(null);
      setProfileError(null);
    }
  }, [selectedCustomerId, loadProfile]);

  const viewProfile = (c) => {
    setSelectedCustomerId(c.id);
  };

  const closeProfileModal = () => {
    setSelectedCustomerId(null);
    setProfileData(null);
    setProfileError(null);
    setExtraVisits([]);
  };

  const loadMoreVisits = async () => {
    if (!selectedCustomerId || !profileData || visitsLoadMoreLoading) return;
    const offset = (profileData.visits?.length || 0) + extraVisits.length;
    setVisitsLoadMoreLoading(true);
    try {
      const r = await api.get(`/customers/${selectedCustomerId}/visits`, { params: { offset, limit: 20 } });
      setExtraVisits(prev => [...prev, ...(r.data.visits || [])]);
    } catch {
      toast.error(t('profile_load_error'));
    } finally {
      setVisitsLoadMoreLoading(false);
    }
  };

  const handleProfileEdit = async () => {
    if (!profileData?.id) return;
    try {
      const r = await api.get(`/customers/${profileData.id}`);
      closeProfileModal();
      openEdit(r.data);
    } catch {
      toast.error(t('cust_edit_load_error'));
    }
  };

  const handleProfileDeactivate = () => {
    if (profileData?.id) {
      setDeactivateConfirmId(profileData.id);
      closeProfileModal();
    }
  };

  const paymentLabel = (method) => {
    const m = (method || '').toLowerCase();
    if (m === 'cash') return t('payment_cash');
    if (m === 'card') return t('payment_card');
    if (m === 'upi') return t('payment_upi');
    return method || '—';
  };

  const formatLastVisit = (dateIso) => {
    if (!dateIso) return t('no_visits_yet');
    const d = new Date(dateIso);
    if (Number.isNaN(d.getTime())) return t('no_visits_yet');
    return d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  const handleDeactivate = async () => {
    if (!deactivateConfirmId) return;
    try {
      await api.patch(`/customers/${deactivateConfirmId}/deactivate`);
      setCustomers(prev => prev.filter(c => c.id !== deactivateConfirmId));
      setDeactivateConfirmId(null);
      toast.success(t('customer_deactivated_toast'));
    } catch (err) {
      toast.error(err.response?.data?.detail || t('cust_deactivate_error'));
      setDeactivateConfirmId(null);
    }
  };

  const handleReactivate = async (c, e) => {
    e?.stopPropagation();
    try {
      const r = await api.patch(`/customers/${c.id}/reactivate`);
      setCustomers(prev => prev.map(x => x.id === c.id ? r.data : x));
      toast.success(t('customer_reactivated_toast'));
    } catch (err) {
      toast.error(err.response?.data?.detail || t('cust_reactivate_error'));
    }
  };

  const filtered = customers.filter(c =>
    c.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    c.phone.includes(searchTerm)
  );

  if (loading) {
    return <div className="space-y-4">{[1,2,3,4].map(i => <Skeleton key={i} className="h-20 rounded-2xl" />)}</div>;
  }

  return (
    <div className="space-y-4 sm:space-y-6 px-1 sm:px-0">
      {/* Header */}
      <div className="flex flex-col gap-3 sm:gap-4">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4">
          <div className="relative w-full sm:flex-1 sm:max-w-md">
            <Search className="absolute left-3 sm:left-4 top-1/2 -translate-y-1/2 w-4 h-4 sm:w-5 sm:h-5 text-gray-400" />
            <Input
              data-testid="customer-search"
              placeholder={t('cust_search')}
              className="h-12 sm:h-14 pl-10 sm:pl-12 text-base sm:text-lg rounded-xl"
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
            />
          </div>
          <Button
            data-testid="add-customer-btn"
            onClick={() => { setEditingId(null); setForm(emptyForm); setFieldErrors({}); setDialogOpen(true); }}
            className="h-12 sm:h-14 px-4 sm:px-6 rounded-xl bg-[#f0a500] hover:bg-[#d69400] text-[#1a1a2e] text-base sm:text-lg font-semibold gap-2 w-full sm:w-auto"
          >
            <Plus className="w-4 h-4 sm:w-5 sm:h-5" /> {t('cust_add')}
          </Button>
        </div>
        {canDeactivate && (
          <label className="flex items-center gap-3 cursor-pointer select-none">
            <Checkbox
              checked={includeInactive}
              onCheckedChange={(checked) => setIncludeInactive(!!checked)}
              className="rounded border-gray-300 h-5 w-5"
            />
            <span className="text-base text-gray-600">{t('show_inactive_customers')}</span>
          </label>
        )}
      </div>

      {/* Customer List */}
      <div className="space-y-2 sm:space-y-3">
        {filtered.length === 0 ? (
          <Card className="border-0 shadow-sm rounded-2xl"><CardContent className="py-10 sm:py-12 text-center text-gray-400 text-base sm:text-lg px-4">{t('cust_none')}</CardContent></Card>
        ) : filtered.map(c => {
          const isInactive = c.is_active === false;
          return (
          <Card
            key={c.id}
            className={`border-0 shadow-sm rounded-xl sm:rounded-2xl transition-all cursor-pointer ${isInactive ? 'bg-gray-50 opacity-90 hover:shadow-md' : 'hover:shadow-md'}`}
            onClick={() => !isInactive && viewProfile(c)}
          >
            <CardContent className="p-4 sm:p-5">
              <div className="flex items-start sm:items-center justify-between gap-2 sm:gap-3">
                <div className="flex items-center gap-3 sm:gap-4 min-w-0 flex-1 overflow-hidden">
                  <div className={`w-10 h-10 sm:w-12 sm:h-12 rounded-full flex items-center justify-center text-white font-bold text-base sm:text-lg flex-shrink-0 ${isInactive ? 'bg-gray-400' : 'bg-[#1a1a2e]'}`}>
                    {c.name[0]}
                  </div>
                  <div className="min-w-0 flex-1 overflow-hidden">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className={`font-semibold text-base sm:text-lg truncate ${isInactive ? 'text-gray-500' : 'text-[#1a1a2e]'}`}>{c.name}</p>
                      <Badge data-testid={`tier-badge-${c.id}`} className="text-xs flex-shrink-0" style={tierBadgeStyles[calculateTier(c.loyalty_points, tierThresholds)] || tierBadgeStyles.Bronze}>{calculateTier(c.loyalty_points, tierThresholds)}</Badge>
                      {isInactive && (
                        <Badge variant="secondary" className="bg-gray-200 text-gray-600 text-xs">{t('customer_inactive_badge')}</Badge>
                      )}
                    </div>
                    <div className="flex items-center gap-2 sm:gap-3 text-xs sm:text-sm text-gray-500 mt-0.5 flex-wrap">
                      <span className="flex items-center gap-1 truncate"><Phone className="w-3 h-3 sm:w-3.5 sm:h-3.5 flex-shrink-0" />{c.phone}</span>
                      {c.email && <span className="hidden sm:inline-flex items-center gap-1 truncate"><Mail className="w-3.5 h-3.5 flex-shrink-0" />{c.email}</span>}
                    </div>
                    {c.notes && (
                      <div className="mt-1 text-xs text-gray-500 italic flex items-start gap-1 min-w-0">
                        <FileText className="w-3 h-3 flex-shrink-0 mt-0.5" />
                        <span className="truncate" title={c.notes}>&quot;{(c.notes || '').length > 30 ? (c.notes || '').slice(0, 30) + '...' : (c.notes || '')}&quot;</span>
                      </div>
                    )}
                  </div>
                </div>
                <div className="flex flex-col items-end gap-1 sm:flex-row sm:items-center sm:gap-2 flex-shrink-0">
                  <div className="text-right sm:block">
                    <p className="text-xs sm:text-sm text-gray-500">{c.visit_count} {t('cust_visits')}</p>
                    <p className={`font-semibold text-sm ${isInactive ? 'text-gray-500' : 'text-[#1a1a2e]'}`}>₹{(c.total_spent || 0).toLocaleString('en-IN')}</p>
                  </div>
                  {isInactive && canDeactivate && (
                    <Button
                      variant="outline"
                      size="sm"
                      className="h-10 rounded-xl border-[#f0a500] text-[#f0a500] hover:bg-[#f0a500]/10 gap-1.5 hidden sm:inline-flex"
                      onClick={(e) => handleReactivate(c, e)}
                    >
                      <RotateCcw className="w-4 h-4" /> {t('customer_reactivate')}
                    </Button>
                  )}
                  <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="w-9 h-9 sm:w-10 sm:h-10 rounded-xl flex-shrink-0"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <MoreVertical className="w-4 h-4 text-gray-500" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="rounded-xl min-w-[180px]">
                        <DropdownMenuItem onClick={(e) => { e.stopPropagation(); openEdit(c); }} className="rounded-lg gap-2 cursor-pointer" data-testid={`edit-customer-${c.id}`}>
                          <Edit className="w-4 h-4" /> {t('edit')}
                        </DropdownMenuItem>
                        {!isInactive && canDeactivate && (
                          <DropdownMenuItem
                            onClick={(e) => { e.stopPropagation(); setDeactivateConfirmId(c.id); }}
                            className="rounded-lg gap-2 cursor-pointer text-amber-700 focus:text-amber-800"
                          >
                            <UserX className="w-4 h-4" /> {t('customer_deactivate')}
                          </DropdownMenuItem>
                        )}
                      </DropdownMenuContent>
                    </DropdownMenu>
                </div>
              </div>
            </CardContent>
          </Card>
          );
        })}
      </div>

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-[#faf8f5] rounded-2xl sm:rounded-3xl max-w-lg w-[calc(100vw-2rem)] max-h-[90vh] overflow-y-auto p-4 sm:p-6">
          <DialogHeader>
            <DialogTitle className="font-poppins text-lg sm:text-xl">{editingId ? t('cust_edit') : t('cust_add')}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-2 sm:mt-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label className="text-base mb-1.5 block">{t('name')} *</Label>
                <Input data-testid="customer-form-name" className="h-11 sm:h-12 rounded-xl" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} />
                {fieldErrors.name && <p className="text-red-500 text-sm mt-1">{fieldErrors.name}</p>}
              </div>
              <div>
                <Label className="text-base mb-1.5 block">{t('phone')} *</Label>
                <Input
                  data-testid="customer-form-phone"
                  type="tel"
                  inputMode="numeric"
                  maxLength={10}
                  className="h-11 sm:h-12 rounded-xl"
                  value={form.phone}
                  onChange={e => setForm(f => ({ ...f, phone: e.target.value.replace(/\D/g, '').slice(0, 10) }))}
                />
                {fieldErrors.phone && <p className="text-red-500 text-sm mt-1">{fieldErrors.phone}</p>}
              </div>
            </div>
            <div>
              <Label className="text-base mb-1.5 block">{t('email')}</Label>
              <Input data-testid="customer-form-email" type="email" className="h-11 sm:h-12 rounded-xl" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} />
              {fieldErrors.email && <p className="text-red-500 text-sm mt-1">{fieldErrors.email}</p>}
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label className="text-base mb-1.5 block">{t('cust_birthday')}</Label>
                <Input data-testid="customer-form-birthday" type="date" className="h-11 sm:h-12 rounded-xl" value={form.birthday} onChange={e => setForm(f => ({ ...f, birthday: e.target.value }))} />
              </div>
              <div>
                <Label className="text-base mb-1.5 block">{t('cust_gender')}</Label>
                <Input data-testid="customer-form-gender" className="h-11 sm:h-12 rounded-xl" placeholder="M / F / Other" value={form.gender} onChange={e => setForm(f => ({ ...f, gender: e.target.value }))} />
              </div>
            </div>
            <div>
              <Label className="text-base mb-1.5 block">{t('notes')}</Label>
              <Input data-testid="customer-form-notes" className="h-11 sm:h-12 rounded-xl" placeholder={t('cust_prefs')} value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} />
            </div>
          </div>
          <div className="flex flex-col-reverse sm:flex-row gap-3 justify-end pt-4">
            <Button variant="outline" onClick={() => setDialogOpen(false)} className="h-11 sm:h-12 px-6 rounded-xl text-base w-full sm:w-auto">{t('cancel')}</Button>
            <Button data-testid="customer-form-save-btn" onClick={handleSave} disabled={saving} className="h-11 sm:h-12 px-8 rounded-xl bg-[#1a1a2e] hover:bg-[#2d2d44] text-white text-base w-full sm:w-auto">
              {saving ? t('saving') : t('save')}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Customer Profile Modal — bottom sheet on mobile, centered on desktop; footer inside panel */}
      <Dialog open={!!selectedCustomerId} onOpenChange={(open) => !open && closeProfileModal()}>
        <DialogContent className="flex flex-col overflow-hidden p-0 gap-0 border-0 bg-[#faf8f5] fixed left-0 right-0 bottom-0 top-auto w-full max-w-full translate-x-0 translate-y-0 h-[95vh] max-h-[95vh] rounded-t-[16px] rounded-b-none sm:left-[50%] sm:top-[50%] sm:right-auto sm:bottom-auto sm:translate-x-[-50%] sm:translate-y-[-50%] sm:w-[95vw] sm:max-w-[800px] sm:h-[90vh] sm:max-h-[90vh] sm:rounded-2xl shadow-xl z-50">
          {profileLoading && !profileData && (
            <div className="p-4 sm:p-6 space-y-4 sm:space-y-6 pt-14 sm:pt-6">
              <div className="flex items-center gap-3 sm:gap-4">
                <Skeleton className="w-12 h-12 sm:w-16 sm:h-16 rounded-full flex-shrink-0" />
                <div className="space-y-2 flex-1">
                  <Skeleton className="h-6 sm:h-8 w-32 sm:w-48" />
                  <Skeleton className="h-4 sm:h-5 w-24 sm:w-32" />
                </div>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4">
                {[1,2,3,4].map(i => <Skeleton key={i} className="h-20 sm:h-24 rounded-xl sm:rounded-2xl" />)}
              </div>
              <Skeleton className="h-16 sm:h-20 rounded-xl sm:rounded-2xl w-full" />
              <div className="space-y-2">
                <Skeleton className="h-5 sm:h-6 w-24 sm:w-32" />
                {[1,2,3,4,5].map(i => <Skeleton key={i} className="h-14 sm:h-16 rounded-lg sm:rounded-xl w-full" />)}
              </div>
            </div>
          )}

          {profileError && !profileData && (
            <div className="p-6 sm:p-8 text-center pt-14 sm:pt-8">
              <p className="text-[#1a1a2e] font-medium mb-4 text-base sm:text-lg">{t('profile_load_error')}</p>
              <Button onClick={() => loadProfile(selectedCustomerId)} className="rounded-xl bg-[#f0a500] hover:bg-[#d69400] text-[#1a1a2e] font-semibold w-full sm:w-auto" data-testid="profile-retry-btn">
                {t('profile_retry')}
              </Button>
            </div>
          )}

          {profileData && !profileLoading && (
            <>
              {/* Header — safe area on mobile, room for close button */}
              <div className="p-4 sm:p-6 pb-3 sm:pb-4 pt-14 sm:pt-6 pr-12 sm:pr-6 border-b border-gray-200/80 bg-white/50 shrink-0">
                <div className="flex items-start justify-between gap-2 sm:gap-4">
                  <div className="flex items-center gap-3 sm:gap-4 flex-1 min-w-0">
                    <div className="w-14 h-14 sm:w-[72px] sm:h-[72px] rounded-full bg-[#1a1a2e] flex items-center justify-center text-white font-bold text-xl sm:text-2xl flex-shrink-0 shadow-md">
                      {(profileData.name || '?')[0].toUpperCase()}
                    </div>
                    <div className="min-w-0 flex-1">
                      <h2 className="font-poppins text-lg sm:text-xl md:text-2xl font-bold text-[#1a1a2e] truncate leading-tight">{profileData.name}</h2>
                      <div className="flex items-center gap-2 flex-wrap mt-1.5 gap-y-0.5">
                        <Badge className="text-xs font-medium" style={tierBadgeStyles[calculateTier(profileData.loyalty_points, tierThresholds)] || tierBadgeStyles.Bronze}>{calculateTier(profileData.loyalty_points, tierThresholds)}</Badge>
                        {profileData.is_active === false && (
                          <Badge variant="secondary" className="bg-gray-200 text-gray-600 text-xs">{t('customer_inactive_badge')}</Badge>
                        )}
                      </div>
                      <div className="flex flex-col sm:flex-row sm:flex-wrap sm:items-center gap-0.5 sm:gap-x-3 sm:gap-y-0 text-xs sm:text-sm text-gray-600 mt-2">
                        <span className="flex items-center gap-1.5 min-w-0 truncate"><Phone className="w-3.5 h-3.5 flex-shrink-0 text-gray-400" />{profileData.phone}</span>
                        {profileData.email && <span className="flex items-center gap-1.5 min-w-0 truncate"><Mail className="w-3.5 h-3.5 flex-shrink-0 text-gray-400" />{profileData.email}</span>}
                      </div>
                      {profileData.created_at && (
                        <p className="text-xs sm:text-sm text-gray-500 mt-1.5">{t('customer_since')} {new Date(profileData.created_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}</p>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-1 flex-shrink-0 -mr-2 sm:mr-0">
                    {canDeactivate && (
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" className="w-9 h-9 sm:w-10 sm:h-10 rounded-xl" onClick={(e) => e.stopPropagation()} data-testid="profile-actions-menu">
                            <MoreVertical className="w-4 h-4 sm:w-5 sm:h-5 text-gray-500" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="rounded-xl min-w-[180px]">
                          <DropdownMenuItem onClick={handleProfileEdit} className="rounded-lg gap-2 cursor-pointer">
                            <Edit className="w-4 h-4" /> {t('edit')} {t('nav_customers').slice(0, -1)}
                          </DropdownMenuItem>
                          {profileData.is_active !== false && (
                            <DropdownMenuItem onClick={handleProfileDeactivate} className="rounded-lg gap-2 cursor-pointer text-amber-700 focus:text-amber-800">
                              <UserX className="w-4 h-4" /> {t('customer_deactivate')}
                            </DropdownMenuItem>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    )}
                  </div>
                </div>
              </div>

              {/* Scrollable content — flex-1 + min-h-0 so footer stays in view */}
              <div className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden overscroll-y-auto" style={{ WebkitOverflowScrolling: 'touch' }}>
              <div className="p-4 sm:p-6 pb-6 sm:pb-8 space-y-4 sm:space-y-6">
                {/* Stats row — 2×2 on mobile with larger tap area, 4 on desktop */}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5 sm:gap-4">
                  <Card className="border border-gray-100/80 shadow-[0_2px_12px_-4px_rgba(0,0,0,0.06)] rounded-xl sm:rounded-2xl">
                    <CardContent className="p-3 sm:p-4 text-center py-4 sm:py-4">
                      <p className="text-xs sm:text-sm text-gray-500 font-medium mb-0.5 sm:mb-1">{t('total_visits')}</p>
                      <p className="font-poppins text-xl sm:text-2xl font-bold text-[#1a1a2e] tabular-nums">{profileData.total_visits ?? 0}</p>
                    </CardContent>
                  </Card>
                  <Card className="border border-gray-100/80 shadow-[0_2px_12px_-4px_rgba(0,0,0,0.06)] rounded-xl sm:rounded-2xl">
                    <CardContent className="p-3 sm:p-4 text-center py-4 sm:py-4">
                      <p className="text-xs sm:text-sm text-gray-500 font-medium mb-0.5 sm:mb-1">{t('total_spent')}</p>
                      <p className="font-poppins text-lg sm:text-2xl font-bold text-[#1a1a2e] tabular-nums">₹{(profileData.total_spent ?? 0).toLocaleString('en-IN')}</p>
                    </CardContent>
                  </Card>
                  <Card className="border border-gray-100/80 shadow-[0_2px_12px_-4px_rgba(0,0,0,0.06)] rounded-xl sm:rounded-2xl">
                    <CardContent className="p-3 sm:p-4 text-center py-4 sm:py-4">
                      <p className="text-xs sm:text-sm text-gray-500 font-medium mb-0.5 sm:mb-1">{t('avg_bill')}</p>
                      <p className="font-poppins text-lg sm:text-2xl font-bold text-[#1a1a2e] tabular-nums">₹{Math.round(Number(profileData.average_bill) || 0).toLocaleString('en-IN')}</p>
                    </CardContent>
                  </Card>
                  <Card className="border border-gray-100/80 shadow-[0_2px_12px_-4px_rgba(0,0,0,0.06)] rounded-xl sm:rounded-2xl">
                    <CardContent className="p-3 sm:p-4 text-center py-4 sm:py-4">
                      <p className="text-xs sm:text-sm text-gray-500 font-medium mb-0.5 sm:mb-1">{t('last_visit')}</p>
                      <p className="font-poppins text-sm sm:text-lg font-bold text-[#1a1a2e] leading-tight">{formatLastVisit(profileData.last_visit)}</p>
                    </CardContent>
                  </Card>
                </div>

                {/* Customer Details — improved UI, mobile-friendly */}
                {(profileData.birthday || profileData.gender || profileData.notes) && (
                  <Card className="border border-gray-100 shadow-[0_2px_12px_-4px_rgba(0,0,0,0.08)] rounded-xl sm:rounded-2xl overflow-hidden bg-white">
                    <CardContent className="p-0">
                      <div className="px-4 py-3 sm:px-5 sm:py-4 border-b border-gray-100 bg-gray-50/60">
                        <h4 className="font-poppins font-semibold text-[15px] sm:text-base text-[#1a1a2e]">{t('customer_details')}</h4>
                      </div>
                      <div className="divide-y divide-gray-100">
                        <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-4 px-4 py-3 sm:px-5 sm:py-3.5 min-h-[44px] sm:min-h-0">
                          <span className="text-xs sm:text-sm text-gray-500 font-medium flex items-center gap-2 shrink-0">
                            <span className="text-base" aria-hidden>🎂</span> {t('birthday')}
                          </span>
                          <span className="text-sm sm:text-base text-[#1a1a2e] pl-6 sm:pl-0">
                            {profileData.birthday ? new Date(profileData.birthday + 'T12:00:00').toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : t('not_provided')}
                          </span>
                        </div>
                        <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-4 px-4 py-3 sm:px-5 sm:py-3.5 min-h-[44px] sm:min-h-0">
                          <span className="text-xs sm:text-sm text-gray-500 font-medium flex items-center gap-2 shrink-0">
                            <span className="text-base" aria-hidden>👤</span> {t('gender')}
                          </span>
                          <span className="text-sm sm:text-base text-[#1a1a2e] pl-6 sm:pl-0 capitalize">
                            {profileData.gender || t('not_provided')}
                          </span>
                        </div>
                        <div className="px-4 py-3 sm:px-5 sm:py-3.5">
                          <span className="text-xs sm:text-sm text-gray-500 font-medium flex items-center gap-2 mb-2">
                            <span className="text-base" aria-hidden>📝</span> {t('notes_label')}
                          </span>
                          <p className="text-sm sm:text-base text-[#1a1a2e] whitespace-pre-wrap leading-relaxed bg-gray-50/80 rounded-lg p-3 sm:p-3.5 border border-gray-100">
                            {profileData.notes || t('no_notes')}
                          </p>
                          <button
                            type="button"
                            onClick={handleProfileEdit}
                            className="mt-3 flex items-center gap-2 text-sm font-medium text-[#f0a500] hover:text-[#d69400] hover:underline py-2 px-1 -ml-1 rounded-lg hover:bg-amber-50/80 min-h-[44px] sm:min-h-0 sm:py-1 sm:mt-2"
                          >
                            <Edit className="w-4 h-4" /> {t('edit')}
                          </button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Loyalty */}
                <Card className="border border-gray-100 shadow-[0_2px_12px_-4px_rgba(0,0,0,0.06)] rounded-xl sm:rounded-2xl bg-white">
                  <CardContent className="p-4 sm:p-5">
                    <h4 className="font-poppins font-semibold text-[15px] sm:text-base text-[#1a1a2e] flex items-center gap-2 mb-2 sm:mb-3 pb-2 border-b border-gray-100">
                      <Star className="w-4 h-4 sm:w-5 sm:h-5 text-[#f0a500]" /> {t('loyalty_points')}
                    </h4>
                    <p className="text-[#1a1a2e] font-medium">
                      {(profileData.loyalty_points ?? 0).toLocaleString('en-IN')} {t('loyalty_points').toLowerCase()} = ₹{(profileData.points_value ?? 0).toLocaleString('en-IN')} {t('points_value').toLowerCase()}
                    </p>
                    {(() => {
                      const points = Number(profileData.loyalty_points) || 0;
                      const info = getNextTierInfo(points, tierThresholds);
                      const percent = getTierProgressPercent(points, tierThresholds);
                      const isDiamond = info.next === null;
                      const tierLabel = { Silver: t('tier_silver'), Gold: t('tier_gold'), Diamond: t('tier_diamond') };
                      return (
                        <div className="mt-3">
                          <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                            <div
                              className="h-full rounded-full transition-all"
                              style={{
                                width: `${percent}%`,
                                backgroundColor: isDiamond ? '#f0a500' : '#f0a500',
                              }}
                            />
                          </div>
                          <p className="text-sm text-gray-500 mt-1.5">
                            {isDiamond ? t('top_tier_achieved') : t('pts_to_next_tier', { count: info.needed, tier: tierLabel[info.next] })}
                          </p>
                        </div>
                      );
                    })()}
                    <div className="mt-3 pt-3 border-t border-gray-100">
                      <button
                        type="button"
                        onClick={() => { closeProfileModal(); navigate('/settings?tab=billing'); }}
                        className="text-sm text-gray-500 hover:text-gray-700 no-underline"
                      >
                        {t('how_tiers_work_link')}
                      </button>
                    </div>
                  </CardContent>
                </Card>

                {/* This month */}
                <div className="flex flex-wrap gap-1.5 sm:gap-2 text-sm sm:text-base text-gray-600">
                  <span className="font-medium text-[#1a1a2e]">{t('this_month')}:</span>
                  <span>{profileData.this_month_visits ?? 0} {t('this_month_visits')} | ₹{(profileData.this_month_spent ?? 0).toLocaleString('en-IN')} {t('this_month_spent')}</span>
                </div>

                {/* Top services */}
                <div>
                  <h4 className="font-poppins font-semibold text-[15px] sm:text-base text-[#1a1a2e] mb-2 pb-2 border-b border-gray-200">{t('top_services')}</h4>
                  <div className="flex flex-wrap gap-2">
                    {(profileData.top_services || []).length === 0 ? (
                      <p className="text-gray-400 text-sm">{t('no_visits_yet')}</p>
                    ) : (
                      (profileData.top_services || []).map((s, i) => (
                        <Badge key={i} variant="secondary" className="rounded-full px-2.5 sm:px-3 py-0.5 sm:py-1 text-xs sm:text-sm bg-white border border-gray-100 shadow-sm">
                          {s.name} ×{s.count}
                        </Badge>
                      ))
                    )}
                  </div>
                </div>

                {/* Bookings — 2-column compact cards grid */}
                {(profileData.bookings?.length || 0) > 0 && (
                  <div>
                    <h4 className="font-poppins font-semibold text-[15px] sm:text-base text-[#1a1a2e] mb-2 sm:mb-3 pb-2 border-b border-gray-200">{t('bookings_count', { count: profileData.bookings.length })}</h4>
                    <div
                      className="grid grid-cols-1 sm:grid-cols-2 gap-2.5 max-h-[280px] overflow-y-auto pr-1"
                      style={{ scrollbarWidth: 'thin', scrollbarColor: '#e0e0e0 transparent', WebkitOverflowScrolling: 'touch' }}
                    >
                      {profileData.bookings.map((bk, i) => {
                        const status = (bk.status || 'scheduled').toLowerCase();
                        const statusStyle = status === 'completed' ? { background: '#E9F7EF', color: '#1A7F46', fontSize: '11px', padding: '2px 6px', borderRadius: '4px' } : status === 'cancelled' ? { background: '#FEECEC', color: '#C0392B', fontSize: '11px', padding: '2px 6px', borderRadius: '4px' } : status === 'no_show' ? { background: '#FFF3E0', color: '#E65100', fontSize: '11px', padding: '2px 6px', borderRadius: '4px' } : { background: '#EBF5FF', color: '#1D70B8', fontSize: '11px', padding: '2px 6px', borderRadius: '4px' };
                        const statusLabel = status === 'completed' ? t('booking_completed') : status === 'cancelled' ? t('booking_cancelled') : status === 'no_show' ? t('booking_no_show') : t('booking_scheduled');
                        const dateTimeStr = bk.date
                          ? `${new Date(bk.date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}${bk.time_slot ? `, ${bk.time_slot}` : ''}`
                          : t('not_provided');
                        return (
                          <div key={bk.id || i} className="bg-white border border-[#f0f0f0] rounded-[10px] p-3 shadow-[0_1px_3px_rgba(0,0,0,0.06)]">
                            <div className="flex items-start justify-between gap-2 mb-1">
                              <span className="text-[12px] text-gray-500">{dateTimeStr}</span>
                              <span className="font-medium shrink-0" style={statusStyle}>{statusLabel}</span>
                            </div>
                            <p className="text-[13px] text-[#1a1a2e] font-medium truncate">✂ {bk.service_names?.length ? bk.service_names.join(', ') : '—'}</p>
                            {bk.staff_name && <p className="text-[12px] text-gray-500 mt-1">👤 {bk.staff_name}</p>}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Visit History — 2-column compact cards grid */}
                <div>
                  <h4 className="font-poppins font-semibold text-[15px] sm:text-base text-[#1a1a2e] mb-2 sm:mb-3 pb-2 border-b border-gray-200">{t('visit_history_count', { count: profileData.total_visits ?? 0 })}</h4>
                  {(profileData.visits?.length || 0) === 0 && extraVisits.length === 0 ? (
                    <p className="text-gray-400 text-center py-4 sm:py-6 text-sm sm:text-base">{t('no_visits_yet')}</p>
                  ) : (
                    <>
                      <div
                        className="grid grid-cols-1 sm:grid-cols-2 gap-2.5 max-h-[320px] overflow-y-auto pr-1"
                        style={{ scrollbarWidth: 'thin', scrollbarColor: '#e0e0e0 transparent', WebkitOverflowScrolling: 'touch' }}
                      >
                        {[...(profileData.visits || []), ...extraVisits].map((v, i) => {
                          const pm = (v.payment_method || 'Cash').toLowerCase();
                          const pillStyle = pm === 'cash' ? { background: '#6b7280', color: 'white', fontSize: '11px', padding: '2px 6px', borderRadius: '4px' } : pm === 'card' ? { background: '#2563eb', color: 'white', fontSize: '11px', padding: '2px 6px', borderRadius: '4px' } : pm === 'upi' ? { background: '#059669', color: 'white', fontSize: '11px', padding: '2px 6px', borderRadius: '4px' } : { background: '#7c3aed', color: 'white', fontSize: '11px', padding: '2px 6px', borderRadius: '4px' };
                          return (
                            <div key={i} className="bg-white border border-[#f0f0f0] rounded-[10px] p-3 shadow-[0_1px_3px_rgba(0,0,0,0.06)]">
                              <div className="flex items-start justify-between gap-2 mb-1">
                                <span className="text-[12px] text-gray-500">{v.date ? new Date(v.date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : '—'}</span>
                                <span className="text-[13px] font-bold text-[#c9a227]">₹{(v.total ?? 0).toLocaleString('en-IN')}</span>
                              </div>
                              <p className="text-[13px] text-[#1a1a2e] font-medium truncate">✂ {v.services?.length ? v.services.join(', ') : '—'}</p>
                              <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 mt-1 text-[12px] text-gray-500">
                                <span className="font-medium" style={pillStyle}>{paymentLabel(v.payment_method)}</span>
                                {v.staff_name && <span>👤 {v.staff_name}</span>}
                              </div>
                              {v.loyalty_points_earned > 0 && <p className="text-[12px] text-[#c9a227] font-medium mt-1">⭐ +{v.loyalty_points_earned} pts</p>}
                            </div>
                          );
                        })}
                      </div>
                      {profileData.total_visits > 20 && (profileData.visits?.length || 0) + extraVisits.length < profileData.total_visits && (
                        <div className="mt-2 flex justify-center">
                          <Button variant="outline" size="sm" className="rounded-xl text-sm min-h-[44px] px-5" onClick={loadMoreVisits} disabled={visitsLoadMoreLoading}>
                            {visitsLoadMoreLoading ? '...' : t('load_more_visits')}
                          </Button>
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>
              </div>

                {/* Sticky footer — flex-shrink: 0, inside modal panel; stack on mobile */}
                <div className="flex-shrink-0 flex flex-col sm:flex-row gap-2 sm:gap-3 p-4 sm:px-6 sm:py-4 pt-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] sm:pb-6 border-t border-[#e5e5e5] bg-white">
                  <Button
                    data-testid="profile-new-bill-btn"
                    onClick={() => { closeProfileModal(); navigate('/billing', { state: { customer: { id: profileData.id, name: profileData.name, phone: profileData.phone, email: profileData.email, loyalty_tier: calculateTier(profileData.loyalty_points, tierThresholds), loyalty_points: profileData.loyalty_points } } }); }}
                    className="flex-1 w-full sm:w-auto h-12 min-h-[48px] sm:h-14 rounded-xl bg-[#1a1a2e] hover:bg-[#2d2d44] text-white gap-2 text-sm sm:text-base font-medium active:scale-[0.98]"
                  >
                    <FileText className="w-4 h-4 sm:w-5 sm:h-5 shrink-0" /> {t('new_bill')}
                  </Button>
                  <Button
                    data-testid="profile-new-booking-btn"
                    onClick={() => { closeProfileModal(); navigate('/bookings', { state: { customer: { id: profileData.id, name: profileData.name, phone: profileData.phone } } }); }}
                    className="flex-1 w-full sm:w-auto h-12 min-h-[48px] sm:h-14 rounded-xl bg-[#f0a500] hover:bg-[#d69400] text-[#1a1a2e] gap-2 text-sm sm:text-base font-semibold active:scale-[0.98]"
                  >
                    <CalendarPlus className="w-4 h-4 sm:w-5 sm:h-5 shrink-0" /> {t('new_booking')}
                  </Button>
                </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Deactivate confirmation */}
      <AlertDialog open={!!deactivateConfirmId} onOpenChange={(open) => !open && setDeactivateConfirmId(null)}>
        <AlertDialogContent className="rounded-2xl max-w-md w-[calc(100vw-2rem)] max-h-[90vh] overflow-y-auto p-4 sm:p-6">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-poppins">{t('customer_deactivate_confirm_title')}</AlertDialogTitle>
            <AlertDialogDescription>{t('customer_deactivate_confirm_body')}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-xl">{t('cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeactivate}
              className="bg-amber-500 hover:bg-amber-600 text-[#1a1a2e] rounded-xl font-semibold"
            >
              {t('customer_deactivate_confirm_btn')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
